import React, { useEffect, useState, useContext, useCallback } from 'react';
import UserContext from "../context/UserContext";
import { api } from '../utils/apiHelper';
import '../styles/ShipAddy.css';

const initialForm = {
  recipientName: "",
  phone: "",
  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
  country: "USA",
  neighborhood: "",
  landmark: ""
};

const ShipAddy = () => {
  const { authUser } = useContext(UserContext);

  const [form, setForm] = useState(initialForm);
  const [list, setList] = useState([]);        // saved addresses (array)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const loadShipping = useCallback(async () => {
    if (!authUser?._id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api(`/get-shipping-by-user/${authUser._id}`, 'GET');

      // Accept array or single object; normalize to array
      const arr = Array.isArray(res?.shipping)
        ? res.shipping
        : (res?.shipping ? [res.shipping] : []);

      setList(arr);
    } catch (e) {
      console.error(e);
      setError("Error loading shipping info.");
    } finally {
      setLoading(false);
    }
  }, [authUser?._id]);

  useEffect(() => {
    loadShipping();
  }, [loadShipping]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setSaved(false);
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    const required = ["recipientName", "streetAddress", "city", "state", "zipCode", "country"];
    for (const key of required) {
      if (!form[key]?.trim()) return `${key} is required`;
    }
    if (form.phone && !/^[0-9()+\-.\s]{7,20}$/.test(form.phone)) return "Phone appears invalid";
    if (form.zipCode && !/^[A-Za-z0-9\-\s]{3,12}$/.test(form.zipCode)) return "ZIP/Postal appears invalid";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!authUser?._id) return;

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSaved(false);

      // ✅ Frontend whitelists & trims the payload (backend does a simple $push)
      const {
        recipientName, phone, streetAddress, city, state, zipCode, country, neighborhood, landmark
      } = form;

      const cleanShipping = {
        recipientName: recipientName?.trim() || "",
        phone: phone?.trim() || "",
        streetAddress: streetAddress?.trim() || "",
        city: city?.trim() || "",
        state: state?.trim() || "",
        zipCode: zipCode?.trim() || "",
        country: country || "USA",
        neighborhood: neighborhood?.trim() || "",
        landmark: landmark?.trim() || ""
      };

      const res = await api('/add-shipping', 'POST', {
        userId: authUser._id,
        shipping: cleanShipping
      });

      if (res?.success) {
        setSaved(true);
        setForm(initialForm);
        await loadShipping(); // refresh list after save
      } else {
        setError(res?.message || "Failed to save address.");
      }
    } catch (e) {
      console.error(e);
      setError("Error saving address.");
    } finally {
      setSaving(false);
    }
  };

  if (!authUser?._id) {
    return (
      <div className="shipAddy">
        <div className="shipAddy-notice">Please sign in to manage your shipping information.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="shipAddy">
        <div className="shipAddy-loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="shipAddy">
      <h2 className="shipAddy-title">Shipping Information</h2>

      {error && <div className="shipAddy-error">{error}</div>}
      {saved && <div className="shipAddy-success">Saved successfully.</div>}

      <form className="shipAddy-form" onSubmit={onSubmit} noValidate>
        <div className="shipAddy-row">
          <label>Recipient Name<span className="req">*</span></label>
          <input
            name="recipientName"
            value={form.recipientName}
            onChange={onChange}
            placeholder="Full name"
            maxLength={100}
            required
          />
        </div>

        <div className="shipAddy-row">
          <label>Phone</label>
          <input
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder="(555) 123-4567"
            maxLength={20}
          />
        </div>

        <div className="shipAddy-row">
          <label>Street Address<span className="req">*</span></label>
          <input
            name="streetAddress"
            value={form.streetAddress}
            onChange={onChange}
            placeholder="123 Main St"
            maxLength={180}
            required
          />
        </div>

        <div className="shipAddy-grid">
          <div className="shipAddy-row">
            <label>City<span className="req">*</span></label>
            <input
              name="city"
              value={form.city}
              onChange={onChange}
              placeholder="Portland"
              maxLength={80}
              required
            />
          </div>
          <div className="shipAddy-row">
            <label>State/Province<span className="req">*</span></label>
            <input
              name="state"
              value={form.state}
              onChange={onChange}
              placeholder="OR"
              maxLength={40}
              required
            />
          </div>
          <div className="shipAddy-row">
            <label>ZIP/Postal<span className="req">*</span></label>
            <input
              name="zipCode"
              value={form.zipCode}
              onChange={onChange}
              placeholder="97204"
              maxLength={12}
              required
            />
          </div>
          <div className="shipAddy-row">
            <label>Country<span className="req">*</span></label>
            <select
              name="country"
              value={form.country}
              onChange={onChange}
              required
            >
              <option value="">Select country</option>
              <option value="USA">United States</option>
            </select>
          </div>
        </div>

        <div className="shipAddy-row">
          <label>Neighborhood (optional)</label>
          <input
            name="neighborhood"
            value={form.neighborhood}
            onChange={onChange}
            placeholder="Downtown"
            maxLength={80}
          />
        </div>

        <div className="shipAddy-row">
          <label>Landmark / Intersection (optional)</label>
          <input
            name="landmark"
            value={form.landmark}
            onChange={onChange}
            placeholder="Near Pioneer Courthouse Square"
            maxLength={120}
          />
        </div>

        <div className="shipAddy-actions">
          <button type="submit" className="shipAddy-saveBtn" disabled={saving}>
            {saving ? 'Saving…' : 'Save Address'}
          </button>
        </div>
      </form>

      {list.length > 0 && (
        <div className="shipAddy-list">
          <h3 className="shipAddy-subtitle">Saved Addresses ({list.length})</h3>
          <ul>
            {list.map((addr, i) => (
              <li key={i} className="shipAddy-item">
                <strong>{addr.recipientName || '—'}</strong><br />
                {addr.streetAddress || '—'}<br />
                {addr.city || '—'}, {addr.state || '—'} {addr.zipCode || '—'}<br />
                {addr.country || '—'}{addr.phone ? <> · {addr.phone}</> : null}
                {addr.neighborhood ? <> · {addr.neighborhood}</> : null}
                {addr.landmark ? <> · {addr.landmark}</> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ShipAddy;
